import { useDl } from "../utils/dlToken";
import { useRef, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { PenLine, Check, Eye, RotateCcw, Loader2, Signature, Info } from "lucide-react";
import { openPdf } from "../utils/pdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignDocuments({ t, token, hasApplication, onSignedChange }) {
  const dl = useDl(token);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [savedSig, setSavedSig] = useState("");
  const [editing, setEditing] = useState(false);
  const [savingSig, setSavingSig] = useState(false);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([
        axios.get(`${API}/applicant/signature`, auth),
        axios.get(`${API}/applicant/signable-documents`, auth),
      ]);
      setSavedSig(s.data.signature || "");
      setEditing(!s.data.has_signature);
      setDocs(d.data || []);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const allSigned = docs.length > 0 && docs.every((d) => d.signed);
    onSignedChange?.(allSigned);
  }, [docs, onSignedChange]);

  const ctxSetup = () => {
    const c = canvasRef.current;
    if (!c) return null;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111111";
    return ctx;
  };

  const point = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * (c.width / r.width), y: (src.clientY - r.top) * (c.height / r.height) };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = ctxSetup();
    if (!ctx) return;
    drawing.current = true;
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = point(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    hasInk.current = true;
  };
  const endDraw = () => { drawing.current = false; };

  const clearPad = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
  };

  const saveSig = async () => {
    if (!hasInk.current) { setErr(t.signPadHint); return; }
    setErr(""); setSavingSig(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/applicant/signature`, { signature: dataUrl }, auth);
      setSavedSig(dataUrl);
      setEditing(false);
    } catch { setErr(t.signSaveErr || "Chyba"); }
    finally { setSavingSig(false); }
  };

  const signDoc = async (key) => {
    setErr(""); setBusy(key);
    try {
      await axios.post(`${API}/applicant/sign/${key}`, {}, auth);
      await loadAll();
    } catch (e) {
      setErr(e.response?.data?.detail || t.signNeedFirst);
    } finally { setBusy(""); }
  };

  const readDoc = (key) => window.open(`${API}/applicant/document/${key}/preview?token=${dl}`, "_blank", "noopener");

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-6 mb-8" data-testid="portal-sign-documents">
      <div className="flex items-center gap-2 mb-1">
        <Signature size={18} className="text-[#D4AF37]" />
        <h3 className="font-display text-xl">{t.signTitle}</h3>
      </div>
      <p className="text-white/40 text-sm mb-5">{t.signHint}</p>

      {/* signature creation / preview */}
      <div className="mb-6">
        {editing ? (
          <div>
            <p className="text-white/55 text-xs mb-2">{t.signPadHint}</p>
            <canvas
              ref={canvasRef}
              width={520}
              height={170}
              data-testid="signature-pad"
              onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
              onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
              className="w-full h-[170px] rounded-md bg-white cursor-crosshair touch-none"
              style={{ touchAction: "none" }}
            />
            <div className="flex items-center gap-3 mt-3">
              <button onClick={saveSig} disabled={savingSig} data-testid="signature-save"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-5 py-2.5 text-sm hover:bg-[#F2E3C6] transition-colors disabled:opacity-60">
                {savingSig ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} {t.signSave}
              </button>
              <button onClick={clearPad} data-testid="signature-clear"
                className="inline-flex items-center gap-2 border border-white/15 text-white/70 px-4 py-2.5 text-sm hover:border-white/40 transition-colors">
                <RotateCcw size={14} /> {t.signClear}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-4 rounded-md">
            <div className="flex items-center gap-4 min-w-0">
              <div className="bg-white rounded px-3 py-1 shrink-0">
                <img src={savedSig} alt="signature" className="h-10 object-contain" data-testid="signature-preview" />
              </div>
              <span className="inline-flex items-center gap-1.5 text-green-400 text-sm font-medium">
                <Check size={16} /> {t.signSaved}
              </span>
            </div>
            <button onClick={() => { setEditing(true); setTimeout(clearPad, 50); }} data-testid="signature-change"
              className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold shrink-0 hover:underline">
              <PenLine size={14} /> {t.signChange}
            </button>
          </div>
        )}
      </div>

      {err && <p className="text-red-400 text-sm mb-4" data-testid="sign-error">{err}</p>}

      {!hasApplication && (
        <div className="flex items-start gap-2.5 border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-3 mb-4 rounded-md" data-testid="sign-need-application">
          <Info size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
          <p className="text-white/70 text-sm">{t.signNeedApp}</p>
        </div>
      )}

      {/* documents to sign */}
      <div className="space-y-2">
        {docs.map((d) => (
          <div key={d.key} data-testid={`sign-doc-${d.key}`}
            className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3 rounded-md">
            <div className="min-w-0">
              <p className="text-white/85 text-sm truncate">{d.title}</p>
              {d.signed && <p className="text-green-400/80 text-xs mt-0.5 inline-flex items-center gap-1"><Check size={12} /> {t.signedOn} {(d.signed_at || "").slice(0, 10)}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => readDoc(d.key)} data-testid={`sign-read-${d.key}`}
                className="inline-flex items-center gap-1.5 border border-white/15 text-white/75 px-3 py-2 text-xs hover:border-[#D4AF37]/50 transition-colors rounded">
                <Eye size={13} /> {t.signRead || "Prečítať"}
              </button>
              {d.signed && (
                <button onClick={() => openPdf(`${API}/applicant/signed/${d.key}/pdf?token=${dl}`, { filename: `${d.key}-podpisane-saubereko.pdf` })}
                  data-testid={`sign-view-${d.key}`}
                  className="inline-flex items-center gap-1.5 border border-white/15 text-white/75 px-3 py-2 text-xs hover:border-[#D4AF37]/50 transition-colors rounded">
                  <Eye size={13} /> {t.signView}
                </button>
              )}
              <button onClick={() => signDoc(d.key)} disabled={busy === d.key || editing || !hasApplication}
                data-testid={`sign-btn-${d.key}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  d.signed ? "border border-white/15 text-white/70 hover:border-white/40" : "bg-[#D4AF37] text-black hover:bg-[#F2E3C6]"}`}>
                {busy === d.key ? <Loader2 size={13} className="animate-spin" /> : <PenLine size={13} />}
                {d.signed ? t.signResign : t.signBtn}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
